import { createIntegrationManager } from './core';
import { createMixpanelIntegration } from './providers/mixpanel';
import { createIntercomIntegration } from './providers/intercom';
import {
  createSentryIntegration,
  initializeSentryFromConfig,
} from './providers/sentry';
import type {
  Integration,
  IntegrationManager,
  IntegrationBootstrapConfig,
  IntegrationFactory,
} from './types';
import { checkEmbedMode } from '@/utils/dom';

let integrationManager: IntegrationManager | null = null;
let isBootstrapping = false;

/**
 * Build configuration from environment variables
 */
function buildConfig(): IntegrationBootstrapConfig {
  return Object.freeze({
    isProd: import.meta.env.PROD,
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    mixpanelToken: import.meta.env.VITE_MIXPANEL_TOKEN,
    intercomAppId: import.meta.env.VITE_INTERCOM_APP_ID,
    environment: import.meta.env.MODE,
    isEmbedMode: checkEmbedMode(),
  });
}

/**
 * Define integration factories based on configuration
 */
function defineIntegrationFactories(
  config: IntegrationBootstrapConfig
): Array<{ name: string; enabled: boolean; factory: IntegrationFactory }> {
  return [
    {
      name: 'Mixpanel',
      enabled: config.isProd && !!config.mixpanelToken,
      factory: () =>
        createMixpanelIntegration({
          token: config.mixpanelToken!,
          debug: false,
        }),
    },
    {
      name: 'Intercom',
      enabled: !config.isEmbedMode && !!config.intercomAppId,
      factory: () =>
        createIntercomIntegration({
          appId: config.intercomAppId!,
        }),
    },
    {
      name: 'Sentry',
      enabled: config.isProd,
      factory: () => createSentryIntegration(),
    },
  ];
}

/**
 * Initialize integrations synchronously
 */
function initializeIntegrations(
  config: IntegrationBootstrapConfig
): Integration[] {
  const factories = defineIntegrationFactories(config);
  const enabledFactories = factories.filter(f => f.enabled);

  if (enabledFactories.length === 0) {
    console.log('[Integration] No integrations enabled');
    return [];
  }

  console.log(
    '[Integration] Initializing:',
    enabledFactories.map(f => f.name).join(', ')
  );

  // Initialize all enabled integrations synchronously
  const integrations: Integration[] = [];

  enabledFactories.forEach(({ name, factory }) => {
    try {
      const integration = factory();
      if (integration) {
        integrations.push(integration);
      } else {
        console.warn(`[Integration] ${name} factory returned null`);
      }
    } catch (error) {
      console.error(`[Integration] Failed to initialize ${name}:`, error);
    }
  });

  console.log(
    '[Integration] Successfully initialized:',
    integrations.map(i => i.name).join(', ')
  );

  return integrations;
}

/**
 * Bootstrap all integrations (synchronous)
 * - Initializes Sentry first (must happen before React renders)
 * - Initializes other integrations
 * - Creates and returns the integration manager
 */
const bootstrapIntegrations = (): void => {
  if (integrationManager || isBootstrapping) {
    return;
  }

  isBootstrapping = true;

  try {
    const config = buildConfig();

    // Step 1: Initialize Sentry independently
    // IMPORTANT: This must happen BEFORE React renders to capture startup errors
    initializeSentryFromConfig(config);

    // Step 2: Initialize other integrations synchronously
    const integrations = initializeIntegrations(config);

    // Step 3: Create integration manager
    integrationManager = createIntegrationManager(integrations);

    console.log(
      `[Integration] Bootstrap complete with ${integrations.length} integrations`
    );
  } catch (error) {
    console.error('[Integration] Bootstrap failed:', error);
    throw error;
  } finally {
    isBootstrapping = false;
  }
};

/**
 * Get the integration manager instance
 * @throws Error if integrations have not been bootstrapped
 */
const getIntegrationManager = (): IntegrationManager => {
  if (!integrationManager) {
    throw new Error(
      '[Integration] Manager not initialized. Call bootstrapIntegrations() first.'
    );
  }
  return integrationManager;
};

export { bootstrapIntegrations, getIntegrationManager };
